import { useState } from 'react';
import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { format, isValid, parse } from 'date-fns';
import { Tooltip, IconButton } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSearchParams } from 'react-router-dom';
import { CardButton } from '../../Compounds/Buttons/CardButton';
import { UpstackSourcedRenderer } from '../../Compounds/UpstackSourcedRenderer';
import { booleanOperators, standardOperators } from '../../Compounds/UpstackDataGrid/helpers';
import { getSFHost, termLabel, usaCurrency } from '../../../utils/helpers';
import { BenchmarkStatus } from '../../../store/BenchmarkStatus';
import { getUser } from '../../../store/User';
import { SKIP_ACCOUNT_ROLES } from '../../../store/Role';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { postTask } from '../../../Api/Tasks/tasksApi';

export type PriceBenchmarkTabNames = 'to_market' | 'needs_review';

const formatBenchmarkDate = (value: string | undefined | null): string => {
  if (!value) return '';
  for (const fmt of ['yyyy-MM-dd', 'MM/dd/yyyy']) {
    const parsed = parse(value, fmt, new Date());
    if (isValid(parsed)) return format(parsed, 'MM/dd/yyyy');
  }
  return value;
};

const getBenchmarkStatusMessage = (status: BenchmarkStatus): string =>
  status === BenchmarkStatus.NotAvailable ? 'Not Available' : 'Add Needed Data';

export const PriceBenchmarkTabDisplayNames: Record<PriceBenchmarkTabNames, string> = {
  to_market: 'Price to Market',
  needs_review: 'Needs Review'
};

function BenchmarkAssetDetailCTARenderer({ assetId }: { assetId: string }) {
  const [params, setParams] = useSearchParams();

  const handleClick = () => {
    params.get('assetDetailId') === assetId ? params.delete('assetDetailId') : params.set('assetDetailId', assetId);
    setParams(params, { replace: true });
  };

  return (
    <div className="mt-3">
      <CardButton
        data-testid="detail-cta-button"
        data-cy="detail-cta-button"
        onClick={handleClick}
        className="h-4"
        icon="details"
      />
    </div>
  );
}

function RequiresReviewCell({
  assetId,
  hasExistingTask,
  tasksLoaded,
  onTaskCreated
}: {
  assetId: string;
  hasExistingTask: boolean;
  tasksLoaded: boolean;
  onTaskCreated?: (assetId: string) => void;
}) {
  const [clicked, setClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setSnack } = useSnackbar();

  const handleClick = async () => {
    if (clicked || loading) return;
    setLoading(true);
    const { error } = await postTask({ outside_inventory_id: assetId });
    setLoading(false);
    if (error) {
      const status = error.errorResponse?.response?.status;
      const message =
        status === 422 || status === 409
          ? 'A review request already exists for this service. Contact your UPSTACK Advisor for assistance.'
          : status === 403
            ? 'You do not have permission to request a review for this service.'
            : 'Something went wrong. Please try again.';
      setSnack({ open: true, type: 'error', message });
    } else {
      setClicked(true);
      onTaskCreated?.(assetId);
      setSnack({
        open: true,
        type: 'success',
        message: 'Your request has been submitted. An UPSTACK Advisor will be in touch shortly.'
      });
    }
  };

  const reviewRequested = hasExistingTask || clicked;
  const disabled = reviewRequested || loading || !tasksLoaded;

  return (
    <Tooltip
      title={
        reviewRequested
          ? 'Your request requires a personalized touch — an UPSTACK Advisor will be in touch shortly.'
          : 'Your request requires a personalized touch — click here to request review from an UPSTACK Advisor.'
      }>
      <span
        onClick={disabled ? undefined : handleClick}
        onKeyDown={disabled ? undefined : (e) => (e.key === 'Enter' || e.key === ' ') && handleClick()}
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
        className={`italic text-sm ${reviewRequested ? 'text-grey-6' : 'text-indigo'}`}>
        {reviewRequested ? 'Review Requested' : 'Requires Review'}
      </span>
    </Tooltip>
  );
}

function DataCorrectionCellRenderer({ assetId, sfId, message }: { assetId: string; sfId?: string; message: string }) {
  const [params, setParams] = useSearchParams();

  const handleClick = () => {
    const userCanOpenSF = getUser()?.roles?.some(({ name }) => name && SKIP_ACCOUNT_ROLES.includes(name));
    if (userCanOpenSF && sfId) {
      window.open(`${getSFHost()}/lightning/r/Asset/${encodeURIComponent(sfId)}/view`, '_blank', 'noopener,noreferrer');
      return;
    }
    params.set('assetDetailId', assetId);
    setParams(params, { replace: true });
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
      <Tooltip title={message}>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label={message}
          sx={{ color: 'black' }}>
          <ErrorOutlineIcon
            fontSize="small"
            aria-hidden="true"
          />
        </IconButton>
      </Tooltip>
    </div>
  );
}

export const getBenchMarkColumnsByTab = (
  tabName: PriceBenchmarkTabNames,
  existingTaskAssetIds?: Set<string>,
  tasksLoaded = false,
  onTaskCreated?: (assetId: string) => void
) => {
  switch (tabName) {
    case 'to_market':
      return buildToMarketColumns(existingTaskAssetIds, tasksLoaded, onTaskCreated);
    case 'needs_review':
      return buildNeedsReviewColumns(existingTaskAssetIds, tasksLoaded, onTaskCreated);
    default:
      return buildToMarketColumns(existingTaskAssetIds, tasksLoaded, onTaskCreated);
  }
};

function buildToMarketColumns(
  existingTaskAssetIds?: Set<string>,
  tasksLoaded = false,
  onTaskCreated?: (assetId: string) => void
): GridSingleSelectColDef[] {
  return [
    ...defaultPriceBenchmarkColumns,
    {
      field: 'priceToMarket',
      headerName: 'Price to Market',
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column',
      filterOperators: standardOperators(),
      flex: 1,
      valueFormatter: (value, row) =>
        row?.benchmarkStatus === 'available_with_data_corrections' || row?.benchmarkStatus === 'not_available'
          ? ''
          : `${row.priceToMarket >= 0 ? '+' : ''}${usaCurrency.format(value as number)}`,
      valueGetter: (value, row) =>
        row?.benchmarkStatus === 'available_with_data_corrections' || row?.benchmarkStatus === 'not_available'
          ? null
          : Number(row?.priceToMarket),
      renderCell: (params: GridCellParams) => {
        if (params.row.benchmarkStatus === 'available_with_data_corrections') {
          return <span className="text-grey-6 italic text-sm">Add Needed Data</span>;
        }
        if (params.row.benchmarkStatus === 'not_available') {
          return (
            <RequiresReviewCell
              assetId={params.row.assetId}
              hasExistingTask={!!existingTaskAssetIds?.has(params.row.assetId)}
              tasksLoaded={tasksLoaded}
              onTaskCreated={onTaskCreated}
            />
          );
        }
        const value = Number(params.row?.priceToMarket);
        return <span>{`${value >= 0 ? '+' : ''}${usaCurrency.format(value)}`}</span>;
      }
    } as GridSingleSelectColDef,
    {
      field: 'marketLow',
      headerName: 'Low Alternative',
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column',
      filterOperators: standardOperators(),
      flex: 1,
      valueGetter: (_value: unknown, row: { mrc: number; priceToLowest: number; benchmarkStatus?: string }) =>
        row?.benchmarkStatus === 'available' && row.priceToLowest != null
          ? Number(row.mrc) - Number(row.priceToLowest)
          : null,
      valueFormatter: (value: unknown) =>
        value != null && !isNaN(value as number) ? usaCurrency.format(value as number) : null,
      renderCell: (params: GridCellParams) =>
        params.formattedValue != null ? (
          <span>{params.formattedValue as string}</span>
        ) : (
          <span style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>-</span>
        )
    } as GridSingleSelectColDef,
    {
      field: 'marketHigh',
      headerName: 'High Alternative',
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column',
      filterOperators: standardOperators(),
      flex: 1,
      valueGetter: (_value: unknown, row: { mrc: number; priceToHighest: number; benchmarkStatus?: string }) =>
        row?.benchmarkStatus === 'available' && row.priceToHighest != null
          ? Number(row.mrc) - Number(row.priceToHighest)
          : null,
      valueFormatter: (value: unknown) =>
        value != null && !isNaN(value as number) ? usaCurrency.format(value as number) : null,
      renderCell: (params: GridCellParams) =>
        params.formattedValue != null ? (
          <span>{params.formattedValue as string}</span>
        ) : (
          <span style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}>-</span>
        )
    } as GridSingleSelectColDef,
    ...benchmarkMetaColumns
  ];
}

function buildNeedsReviewColumns(
  existingTaskAssetIds?: Set<string>,
  tasksLoaded = false,
  onTaskCreated?: (assetId: string) => void
): GridSingleSelectColDef[] {
  return [
    ...defaultPriceBenchmarkColumns,
    {
      field: 'priceToMarket',
      headerName: 'Price to Market',
      sortable: false,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column',
      filterOperators: standardOperators(),
      flex: 1,
      valueFormatter: () => '',
      valueGetter: () => null,
      renderCell: (params: GridCellParams) => {
        if (params.row.benchmarkStatus === 'available_with_data_corrections') {
          return <span className="text-grey-6 italic text-sm">Add Needed Data</span>;
        }
        if (params.row.benchmarkStatus === 'not_available') {
          return (
            <RequiresReviewCell
              assetId={params.row.assetId}
              hasExistingTask={!!existingTaskAssetIds?.has(params.row.assetId)}
              tasksLoaded={tasksLoaded}
              onTaskCreated={onTaskCreated}
            />
          );
        }
        return <span className="text-grey-6 italic text-sm">Not Available</span>;
      }
    } as GridSingleSelectColDef,
    {
      field: 'marketLow',
      headerName: 'Low Alternative',
      sortable: false,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column',
      filterOperators: standardOperators(),
      flex: 1,
      valueFormatter: () => '',
      valueGetter: () => null,
      renderCell: () => <span className="text-grey-6 italic text-sm">Add Needed Data</span>
    } as GridSingleSelectColDef,
    {
      field: 'marketHigh',
      headerName: 'High Alternative',
      sortable: false,
      filterable: false,
      type: 'singleSelect',
      valueOptions: [],
      description: 'Drag to move column',
      filterOperators: standardOperators(),
      flex: 1,
      valueFormatter: () => '',
      valueGetter: () => null,
      renderCell: () => <span className="text-grey-6 italic text-sm">Add Needed Data</span>
    } as GridSingleSelectColDef,
    ...benchmarkMetaColumns
  ];
}

const defaultPriceBenchmarkColumns: GridSingleSelectColDef[] = [
  {
    field: 'details',
    headerName: 'Details',
    sortable: false,
    width: 80,
    minWidth: 80,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    hideable: false,
    disableReorder: true,
    renderCell: (params: GridCellParams) => <BenchmarkAssetDetailCTARenderer assetId={params.row.assetId} />
  },
  {
    field: 'origin',
    headerName: 'Origin',
    sortable: true,
    renderCell: (params: GridCellParams) => (
      <UpstackSourcedRenderer
        origin={!!params.value}
        accountName={params.row.accountName}
      />
    ),
    width: 125,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    filterOperators: booleanOperators()
  },
  {
    field: 'address',
    headerName: 'Address',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    flex: 2,
    renderCell: (params: GridCellParams) => {
      if (
        params.row.benchmarkStatus === BenchmarkStatus.UnknownAndRequiresDataCorrections &&
        !params.row.shippingpostalcode
      ) {
        return (
          <DataCorrectionCellRenderer
            assetId={params.row.assetId}
            sfId={params.row.sfId}
            message="No Zip Code data found. Price to Market Calculations may be available if missing fields are added."
          />
        );
      }
      return <span>{params.row.address || ''}</span>;
    }
  },
  {
    field: 'product.name',
    headerName: 'Product',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    valueFormatter: (value, row) =>
      row?.product ? `${row.product.family} - ${row.product.nameAbbreviation || row.product.name}` : '',
    valueGetter: (value, row) => (row?.product ? row.product.nameAbbreviation || row.product.name : ''),
    flex: 1
  },
  {
    field: 'supplier.name',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    valueFormatter: (value, row) => row?.supplier?.name || '',
    valueGetter: (value, row) => row?.supplier?.name || '',
    flex: 1,
    renderCell: (params: GridCellParams) => {
      if (
        params.row.benchmarkStatus === BenchmarkStatus.UnknownAndRequiresDataCorrections &&
        !params.row.supplier_name
      ) {
        return (
          <DataCorrectionCellRenderer
            assetId={params.row.assetId}
            sfId={params.row.sfId}
            message="No Supplier data found. Price to Market Calculations may be available if missing fields are added."
          />
        );
      }
      return <span>{params.row.supplier?.name || ''}</span>;
    }
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    flex: 1,
    valueFormatter: (value) => formatBenchmarkDate(value as string),
    renderCell: (params: GridCellParams) => {
      if (params.row.benchmarkStatus === BenchmarkStatus.AvailableWithDataCorrections && !params.row.startDate) {
        return (
          <DataCorrectionCellRenderer
            assetId={params.row.assetId}
            sfId={params.row.sfId}
            message="No start date found. Please add via Inventory Details to enable Price to Market Calculations."
          />
        );
      }
      if (
        params.row.benchmarkStatus === BenchmarkStatus.UnknownAndRequiresDataCorrections &&
        !params.row.billing_commencement_date_c &&
        !params.row.completiondate &&
        !params.row.startDate
      ) {
        return (
          <DataCorrectionCellRenderer
            assetId={params.row.assetId}
            sfId={params.row.sfId}
            message="No Start Date data found. Price to Market Calculations may be available if missing fields are added."
          />
        );
      }
      return <span>{formatBenchmarkDate(params.row.startDate)}</span>;
    }
  },
  {
    field: 'speed',
    headerName: 'Speed',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    flex: 1,
    renderCell: (params: GridCellParams) => {
      if (params.row.benchmarkStatus === BenchmarkStatus.AvailableWithDataCorrections && !params.row.speed) {
        return (
          <DataCorrectionCellRenderer
            assetId={params.row.assetId}
            sfId={params.row.sfId}
            message="No speed data found. Please add via Inventory Details to enable Price to Market Calculations."
          />
        );
      }
      if (
        params.row.benchmarkStatus === BenchmarkStatus.UnknownAndRequiresDataCorrections &&
        !params.row.speed_c &&
        !params.row.accessspeed_c &&
        !params.row.port_speed_c &&
        !params.row.bandwidth_mbps_c
      ) {
        return (
          <DataCorrectionCellRenderer
            assetId={params.row.assetId}
            sfId={params.row.sfId}
            message="No Speed data found. Price to Market Calculations may be available if missing fields are added."
          />
        );
      }
      return <span>{params.row.speed || ''}</span>;
    }
  },
  {
    field: 'mrc',
    headerName: 'Monthly Cost',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    flex: 1,
    valueGetter: (value) => Number(value),
    valueFormatter: (value) => usaCurrency.format(value as number)
  }
];

const benchmarkMetaColumns: GridSingleSelectColDef[] = [
  {
    field: 'term',
    headerName: 'Term',
    sortable: true,
    // A getter rather than a formatter, so the filter options and the sort key match the label.
    valueGetter: (value: string) => termLabel(value),
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    flex: 1
  },
  {
    field: 'contractEndDate',
    headerName: 'Est. Contract End Date',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    flex: 1,
    valueFormatter: (value) => formatBenchmarkDate(value as string)
  }
];

export const priceBenchmarkToMarketColumns: GridSingleSelectColDef[] = buildToMarketColumns();

export const priceBenchmarkToSupplierColumns: GridSingleSelectColDef[] = [
  ...defaultPriceBenchmarkColumns,
  {
    field: 'priceToSupplier',
    headerName: 'Price to Recommended Supplier',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    valueGetter: (value, row) =>
      row?.benchmarkStatus !== BenchmarkStatus.Available ? null : Number(row?.priceToSupplier),
    renderCell: (params: GridCellParams) => {
      if (params.row.benchmarkStatus !== BenchmarkStatus.Available) {
        return (
          <span className="text-grey-6 italic text-sm">{getBenchmarkStatusMessage(params.row.benchmarkStatus)}</span>
        );
      }
      return (
        <span>{`${params.row.priceToSupplier >= 0 ? '+' : ''}${usaCurrency.format(params.row.priceToSupplier)} ${params.row.priceToSupplierName}`}</span>
      );
    },
    flex: 2
  },
  ...benchmarkMetaColumns
];

export const priceBenchmarkToLowestColumns: GridSingleSelectColDef[] = [
  ...defaultPriceBenchmarkColumns,
  {
    field: 'priceToLowest',
    headerName: 'Price to Lowest',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    valueGetter: (value, row) =>
      row?.benchmarkStatus !== BenchmarkStatus.Available ? null : Number(row?.priceToLowest),
    renderCell: (params: GridCellParams) => {
      if (params.row.benchmarkStatus !== BenchmarkStatus.Available) {
        return (
          <span className="text-grey-6 italic text-sm">{getBenchmarkStatusMessage(params.row.benchmarkStatus)}</span>
        );
      }
      return (
        <span>{`${params.row.priceToLowest >= 0 ? '+' : ''}${usaCurrency.format(params.row.priceToLowest)} ${params.row.priceToLowestName}`}</span>
      );
    },
    flex: 2
  },
  ...benchmarkMetaColumns
];

export const priceBenchmarkToHighestColumns: GridSingleSelectColDef[] = [
  ...defaultPriceBenchmarkColumns,
  {
    field: 'priceToHighest',
    headerName: 'Price to Highest',
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    description: 'Drag to move column',
    filterOperators: standardOperators(),
    valueGetter: (value, row) =>
      row?.benchmarkStatus !== BenchmarkStatus.Available ? null : Number(row?.priceToHighest),
    renderCell: (params: GridCellParams) => {
      if (params.row.benchmarkStatus !== BenchmarkStatus.Available) {
        return (
          <span className="text-grey-6 italic text-sm">{getBenchmarkStatusMessage(params.row.benchmarkStatus)}</span>
        );
      }
      return (
        <span>{`${params.row.priceToHighest >= 0 ? '+' : ''}${usaCurrency.format(params.row.priceToHighest)} ${params.row.priceToHighestName}`}</span>
      );
    },
    flex: 2
  },
  ...benchmarkMetaColumns
];

export const priceBenchmarkToNeedsReviewColumns: GridSingleSelectColDef[] = buildNeedsReviewColumns();
